import type { AiBriefingListResponse, AiBriefingRunSummary } from "@closer/shared";
import {
  AI_BRIEFING_ADVISORY,
  AI_BRIEFING_EMPTY_MESSAGE,
  AI_BRIEFING_PRIORITY_NAMES,
  AI_BRIEFING_SOURCE_KIND_NAMES,
  AI_BRIEFING_UNVERIFIED_WARNING,
  AI_CERTAINTY_WARNING,
} from "@closer/shared";
import { Badge, Button, Card, InfoBanner, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AiAdvisoryNotice } from "../../components/AiOutputNotice";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * ANA SAYFA GÜNLÜK AI ÖZETİ (CLOS-290 `[194]` · CLOS-1023).
 *
 * ### ⚠ BÖLÜM KAPALI KURULUMDA SESSİZCE KAYBOLUYOR
 *
 * Kartın açık kararı özetin bir bayrakla açılmasını istiyor. Bayrak kapalıyken sunucu
 * adlandırılmış bir cevapla reddediyor; ekran o cevabı bir ARIZA gibi göstermiyor —
 * bölümü hiç çizmiyor. Kırmızı bir hata kutusu, kapalı bir özelliği bozuk gibi
 * gösterirdi.
 *
 * ### ⚠ DOĞRULANMAMIŞ SATIR AYRICA UYARILIYOR
 *
 * Sunucu her satırın kaynağını bugünkü listeyle karşılaştırıyor. Eşleşmeyen satır ekranda
 * uyarı taşıyor: AI'ın gösterdiği iş kullanıcının listesinde yoksa, o satıra göre işlem
 * yapmak yanlış bir kayda dokunmak olabilir.
 *
 * ### ⚠ HİÇBİR EYLEM DÜĞMESİ YOK
 *
 * Kart: *"AI davet göndermez, görünürlük veya kayıt erişimi vermez, yorum paylaşmaz,
 * görev atamaz, onay/ret/mutabakat/imza işlemi yapmaz."* Bu bölümde tek düğme özeti
 * ÜRETEN düğme; her satır kullanıcıyı kendi kanonik ekranına bırakıyor.
 */
export function AiBriefingSection() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();

  const anahtar = tenantKey(tenantId, "gunluk-ai-ozet");
  const liste = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (await api<AiBriefingListResponse>(`/api/home/ai-briefing`, { tenantId })).data,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();

  const uret = useMutation({
    mutationFn: async () =>
      (
        await api<AiBriefingRunSummary>(`/api/home/ai-briefing`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({}),
          headers: { "idempotency-key": idempotency.uret() },
        })
      ).data,
    onSuccess: async (sonuc) => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      queryClient.setQueryData<AiBriefingRunSummary[]>(anahtar, (eski) => [sonuc, ...(eski ?? [])]);
      await queryClient.invalidateQueries({ queryKey: anahtar });
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "subscription-overview"),
      });
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Özet üretilemedi"),
  });

  /**
   * ⚠ KAPALI KURULUM İLK KAREDE BİLİNİYOR: bayrak liste cevabında geliyor. Önce bir
   * tıklama, sonra bir hata beklemek kullanıcıyı olmayan bir özelliğe bastırırdı — ve
   * testte de ölçüldü: tıklama+hata kurgusu tam süitte zaman aşımına düşüyordu.
   */
  if (liste.data && !liste.data.enabled) return null;

  const son = (liste.data?.runs ?? []).find((c) => c.status === "completed") ?? null;

  return (
    <Card>
      <div data-testid="ai-gunluk-bolum" className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">Günün özeti</h2>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={uret.isPending}
          disabled={uret.isPending}
          onClick={() => uret.mutate()}
        >
          {uret.isPending ? "Hazırlanıyor…" : son ? "Yeniden hazırla" : "Günümü özetle"}
        </Button>
      </div>

      <AiAdvisoryNotice />
      <p className="mt-1 text-sm text-fg-muted" data-testid="ai-gunluk-uyari">
        {AI_BRIEFING_ADVISORY}
      </p>

      <div className="mt-4">
        {liste.isPending ? (
          <Skeleton lines={2} />
        ) : !son ? (
          <p className="text-sm text-fg-muted">
            Bugünkü bekleyen işlerinizi, kritik tarihlerinizi ve son sözleşme hareketlerinizi AI ile
            özetleyebilirsiniz.
          </p>
        ) : son.focus.length === 0 ? (
          <p className="text-sm text-fg-muted" data-testid="ai-gunluk-bos">
            {AI_BRIEFING_EMPTY_MESSAGE}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
              <Badge tone="neutral">{son.model}</Badge>
              <span className="tabular-nums">{son.credits} kredi</span>
              <span>{son.sourceCount} kayıt özetlendi</span>
            </div>

            {son.summary ? <p className="text-sm text-fg">{son.summary}</p> : null}

            {son.focus.map((satir, i) => (
              <article
                key={i}
                data-testid={`ai-gunluk-satir-${i}`}
                className={
                  satir.sourceVerified
                    ? "rounded-lg border border-border-subtle p-3"
                    : "rounded-lg border border-warning/50 bg-warning-soft/40 p-3"
                }
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={satir.priority === "high" ? "warning" : "neutral"}>
                    {AI_BRIEFING_PRIORITY_NAMES[satir.priority]}
                  </Badge>
                  <Badge tone="neutral">{AI_BRIEFING_SOURCE_KIND_NAMES[satir.sourceKind]}</Badge>
                  <span className="text-xs text-fg-muted">{satir.sourceLabel}</span>
                </div>
                <h3 className="mt-2 text-sm font-semibold text-fg-heading">{satir.title}</h3>
                <p className="mt-1 text-sm text-fg">{satir.rationale}</p>
                {/*
                  ⚠ KESİN İFADE İŞARETİ (CLOS-1089). Cümle silinmiyor ya da düzeltilmiyor —
                  yanına ne olduğu yazılıyor. Sessizce düzeltmek AI'ın ne dediğini
                  kullanıcıdan gizlemek olurdu; reddetmek kullanıcının kredisini bir
                  kelime seçimi yüzünden yakardı. Risk ailesindeki kararla aynı.
                */}
                {satir.certaintyFlagged ? (
                  <p className="mt-1 text-xs text-warning" data-testid="ai-gunluk-kesinlik">
                    {AI_CERTAINTY_WARNING}
                  </p>
                ) : null}

                {!satir.sourceVerified ? (
                  <p
                    className="mt-2 text-xs text-warning"
                    data-testid={`ai-gunluk-dogrulanmadi-${i}`}
                  >
                    {AI_BRIEFING_UNVERIFIED_WARNING}
                  </p>
                ) : null}
              </article>
            ))}

            {son.reviewNeeded.length > 0 ? (
              <InfoBanner tone="warning">
                <div data-testid="ai-gunluk-belirsizlik">
                  <p className="font-medium">Kesin olmayan noktalar</p>
                  <ul className="mt-1 list-disc pl-5 text-sm">
                    {son.reviewNeeded.map((satir, i) => (
                      <li key={i}>{satir}</li>
                    ))}
                  </ul>
                </div>
              </InfoBanner>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}
